/**
 * FeedbackModal Component
 *
 * Modal for collecting user feedback on AI chat responses.
 * Allows users to rate responses (thumbs up/down) and provide optional comments.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const FeedbackModal = ({ isOpen, onClose, onSubmit, rating, question, response, isSubmitting }) => {
    const { t } = useTranslation();
    const [comment, setComment] = useState('');

    if (!isOpen) return null;

    const handleSubmit = () => {
        onSubmit(comment);
        setComment('');
    };

    const handleClose = () => {
        setComment('');
        onClose();
    };

    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001
    };

    const modalStyle = {
        backgroundColor: 'var(--bg)',
        borderRadius: '12px',
        padding: '24px',
        width: '90%',
        maxWidth: '480px',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)'
    };

    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
    };

    const titleStyle = {
        fontSize: '18px',
        fontWeight: 600,
        color: '#f1f5f9',
        margin: 0
    };

    const sectionStyle = {
        marginBottom: '16px'
    };

    const labelStyle = {
        fontSize: '12px',
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px',
        display: 'block'
    };

    const previewStyle = {
        backgroundColor: 'var(--bg)',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '13px',
        color: '#cbd5e1',
        maxHeight: '100px',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    };

    const textareaStyle = {
        width: '100%',
        minHeight: '100px',
        backgroundColor: 'var(--bg)',
        border: '1px solid #334155',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '14px',
        color: '#f1f5f9',
        resize: 'vertical',
        fontFamily: 'inherit',
        outline: 'none',
        boxSizing: 'border-box'
    };

    const buttonContainerStyle = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '20px'
    };

    const buttonBaseStyle = {
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        border: 'none',
        transition: 'all 0.2s ease'
    };

    const cancelButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: 'var(--border)',
        color: '#f1f5f9'
    };

    const submitButtonStyle = {
        ...buttonBaseStyle,
        backgroundColor: rating === 'positive' ? '#22c55e' : '#ef4444',
        color: 'white',
        opacity: isSubmitting ? 0.7 : 1
    };

    return (
        <div style={overlayStyle} onClick={handleClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={headerStyle}>
                    <h3 style={titleStyle}>
                        {rating === 'positive'
                            ? t('chat.feedback.titlePositive', 'Thanks for the positive feedback!')
                            : t('chat.feedback.titleNegative', 'Help us improve')
                        }
                    </h3>
                </div>

                {/* Question preview */}
                <div style={sectionStyle}>
                    <label style={labelStyle}>
                        {t('chat.feedback.yourQuestion', 'Your question')}
                    </label>
                    <div style={previewStyle}>
                        {question}
                    </div>
                </div>

                {/* Response preview */}
                <div style={sectionStyle}>
                    <label style={labelStyle}>
                        {t('chat.feedback.aiResponse', 'AI response')}
                    </label>
                    <div style={previewStyle}>
                        {response?.substring(0, 300)}{response?.length > 300 ? '...' : ''}
                    </div>
                </div>

                {/* Comment textarea */}
                <div style={sectionStyle}>
                    <label style={labelStyle}>
                        {rating === 'positive'
                            ? t('chat.feedback.whatWorked', 'What worked well? (optional)')
                            : t('chat.feedback.whatWrong', 'What could be improved? (optional)')
                        }
                    </label>
                    <textarea
                        style={textareaStyle}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={rating === 'positive'
                            ? t('chat.feedback.placeholderPositive', 'Tell us what was helpful...')
                            : t('chat.feedback.placeholderNegative', 'Tell us what went wrong or how we can improve...')
                        }
                        maxLength={1000}
                        disabled={isSubmitting}
                    />
                </div>

                {/* Buttons */}
                <div style={buttonContainerStyle}>
                    <button
                        style={cancelButtonStyle}
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                        style={submitButtonStyle}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting
                            ? t('common.submitting', 'Submitting...')
                            : t('chat.feedback.submit', 'Submit Feedback')
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

FeedbackModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    rating: PropTypes.oneOf(['positive', 'negative']),
    question: PropTypes.string,
    response: PropTypes.string,
    isSubmitting: PropTypes.bool
};

FeedbackModal.defaultProps = {
    rating: 'positive',
    question: '',
    response: '',
    isSubmitting: false
};

export default FeedbackModal;

